// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	createStore,
	createActionsHook,
	createStateHook,
} from '@atlassian/react-sweet-state';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	setUserProperties,
	getUserProperty,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { createErrorAnalytics } from '@atlassian/jira-polaris-lib-errors/src/controllers/index.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { BANNER_HEIGHT, BANNER_NAME } from '../../constants.tsx';
import type { State } from './types.tsx';

export const initialState: State = {
	isDismissed: false,
	height: BANNER_HEIGHT,
	shouldShow: true,
	isLoading: true,
};

export const actions = {
	onDismiss:
		(accountId: AccountId): Action<State> =>
		async ({ setState }) => {
			if (!accountId) {
				return;
			}

			try {
				await setUserProperties(accountId, BANNER_NAME, 'false');

				setState({
					isDismissed: true,
				});
			} catch (error) {
				setState({
					isDismissed: true,
				});

				fireErrorAnalytics(
					createErrorAnalytics(
						'polaris.jpd-premium-onboarding.banner-dismissed',
						error instanceof Error ? error : new Error('Unknown error'),
					),
				);
			}
		},
	fetchBannerVisibility:
		(accountId: AccountId): Action<State> =>
		async ({ setState }) => {
			if (!accountId) {
				return;
			}

			try {
				const value = await getUserProperty<boolean>(accountId, BANNER_NAME);
				setState({
					shouldShow: value,
					isLoading: false,
				});
			} catch (error) {
				setState({
					isLoading: false,
				});

				if (
					(error instanceof FetchError && error.statusCode === 404) ||
					isClientFetchError(error)
				) {
					return;
				}

				fireErrorAnalytics(
					createErrorAnalytics(
						'polaris.jpd-premium-onboarding.banner-dismissed',
						error instanceof Error ? error : new Error('Unknown error'),
					),
				);
			}
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	name: 'PolarisPremiumOnboardingBanner',
	initialState,
	actions,
});

export const useJpdPremiumOnboardingBannerState = createStateHook(Store);
export const useJpdPremiumOnboardingBannerActions = createActionsHook(Store);
